import React, { FC, HTMLAttributes, useCallback } from 'react';
import clsx from 'clsx';

type gaps = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface ContainerProps extends HTMLAttributes<HTMLDivElement> {
  columnGapXs?: gaps;
  columnGapSm?: gaps;
  columnGap?: gaps;
  columnGapLg?: gaps;
  columnGapXl?: gaps;
  columnGapXxl?: gaps;
  rowGapXs?: gaps;
  rowGapSm?: gaps;
  rowGap?: gaps;
  rowGapLg?: gaps;
  rowGapXl?: gaps;
  rowGapXxl?: gaps;
}

export const Grid: FC<ContainerProps> = ({
  rowGapXs,
  rowGapSm,
  rowGap = 0,
  rowGapLg,
  rowGapXl,
  rowGapXxl,
  columnGapXs,
  columnGapSm,
  columnGap = 0,
  columnGapLg,
  columnGapXl,
  columnGapXxl,
  children,
  className,
  ...rest
}) => {
  const check = useCallback((n) => {
    if (n === 0) {
      return '0';
    }
    return n;
  }, []);

  const cls = clsx(
    'gridcontainer',
    `gridcontainer__row-gap--xs-${check(rowGapXs) || rowGapSm || rowGap}`,
    `gridcontainer__row-gap--sm-${rowGapSm || rowGap}`,
    `gridcontainer__row-gap-${rowGap}`,
    `gridcontainer__row-gap--lg-${rowGapLg || rowGap}`,
    `gridcontainer__row-gap--xl-${rowGapXl || rowGapLg || rowGap}`,
    `gridcontainer__row-gap--xxl-${rowGapXxl || rowGapXl || rowGapLg || rowGap}`,
    `gridcontainer__column-gap--xs-${check(columnGapXs) || columnGapSm || columnGap}`,
    `gridcontainer__column-gap--sm-${columnGapSm || columnGap}`,
    `gridcontainer__column-gap-${columnGap}`,
    `gridcontainer__column-gap--lg-${columnGapLg || columnGap}`,
    `gridcontainer__column-gap--xl-${columnGapXl || columnGapLg || columnGap}`,
    `gridcontainer__column-gap--xxl-${columnGapXxl || columnGapXl || columnGapLg || columnGap}`,
    className,
  );

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div {...rest} className={cls}>
      {children}
    </div>
  );
};

type cols = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface GridItemProps extends HTMLAttributes<HTMLDivElement> {
  colXs?: cols;
  colSm?: cols;
  col?: cols;
  colLg?: cols;
  colXl?: cols;
  colXxl?: cols;
  justify?: 'start' | 'end' | 'center' | 'stretch';
  align?: 'start' | 'end' | 'center' | 'stretch';
}

export const GridItem: FC<GridItemProps> = ({
  colXs,
  colSm,
  col = 4,
  colLg,
  colXl,
  colXxl,
  children,
  justify = 'stretch',
  align = 'start',
  className,
  ...rest
}) => {
  const cls = clsx(
    className,
    `gridcontainer__grid-item--xs-${colXs || colSm || col}`,
    `gridcontainer__grid-item--sm-${colSm || col}`,
    `gridcontainer__grid-item-${col}`,
    `gridcontainer__grid-item--lg-${colLg || col}`,
    `gridcontainer__grid-item--xl-${colXl || colLg || col}`,
    `gridcontainer__grid-item--xxl-${colXxl || colXl || colLg || col}`,
    `gridcontainer__grid-item-justify-${justify}`,
    `gridcontainer__grid-item-align-${align}`,
  );

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div {...rest} className={cls}>
      {children}
    </div>
  );
};
